import { cast, flow, getSnapshot, types } from 'mobx-state-tree';
import { Crystal } from 'http-requests';
import { RequestStateModel } from '../general-models';
import { CrystalModel, FiltersModel } from './models';

export const lastSearchMST = types.enumeration('LastSearch', ['byId', 'list']);

export const CrystalsStore = types
  .model('CrystalStore', {
    statuses: types.optional(
      types.model('CrystalStoreStatuses', {
        getCrystalList: types.optional(RequestStateModel, {}),
        getCrystalById: types.optional(RequestStateModel, {}),
      }),
      {}
    ),
    filters: types.optional(FiltersModel, {}),
    list: types.optional(types.array(CrystalModel), []),
    current: types.optional(types.maybeNull(CrystalModel), null),
    lastSearch: types.optional(lastSearchMST, 'list'),
  })
  .volatile((self) => ({
    initialSnapshot: getSnapshot(self),
  }))
  .views((self) => ({
    get displayedList() {
      if (self.lastSearch === 'byId') {
        return self.current ? [self.current] : [];
      }

      return self.list;
    },
    getCachedCrystalById(id: string) {
      if (self.current?.kristall_id === id) {
        return self.current;
      }

      return self.list.find((crystal) => crystal.kristall_id === id);
    },
  }))
  .actions((self) => ({
    setCurrent(crystal: Crystal.CrystalDataDTO) {
      self.current = cast(crystal);
    },
    syncCurrentToList() {
      const currentSnapshot = self.current?.listSyncSnapshot;

      if (!currentSnapshot) {
        return;
      }

      const existingIndex = self.list.findIndex(
        (item) => item.kristall_id === currentSnapshot.kristall_id
      );

      if (existingIndex >= 0) {
        self.list.splice(existingIndex, 1, cast(currentSnapshot));
      }
    },
    clearCurrent(syncWithList = false) {
      self.current?.cancelEdit();

      if (syncWithList) {
        this.syncCurrentToList();
      }

      self.current = null;
    },
  }))
  .actions((self) => ({
    /** Получение списка кристаллов */
    getCrystalList: flow(function* (filters?: typeof self.filters.listFilters) {
      try {
        self.list = cast([]);
        self.lastSearch = 'list';

        self.statuses.getCrystalList.loadingStates();

        const listFilters = filters ?? self.filters.listFilters;

        const response: Crystal.CrystalListDTO = yield Crystal.GetCrystalList(
          listFilters
        );

        self.list = cast(response.kristalls);

        self.statuses.getCrystalList.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.statuses.getCrystalList.errorStates();

        return Promise.reject(error);
      } finally {
        self.statuses.getCrystalList.finallyStates();
      }
    }),
    /** Получение кристалла по ID */
    getCrystalById: flow(function* (id?: typeof self.filters.id) {
      const crystalId = id ?? self.filters.idFilter;

      if (!crystalId) {
        return;
      }

      try {
        self.lastSearch = 'byId';
        self.clearCurrent();
        self.statuses.getCrystalById.loadingStates();

        const response: Crystal.CrystalDataDTO = yield Crystal.GetCrystal(
          crystalId
        );

        self.current = cast(response);

        self.statuses.getCrystalById.successStates();

        return self.current;
      } catch (error: any) {
        self.current = null;

        if (error.status === 404) {
          return self.current;
        }

        console.error(error);
        self.statuses.getCrystalById.errorStates();

        throw error;
      } finally {
        self.statuses.getCrystalById.finallyStates();
      }
    }),
  }))
  .actions((self) => ({
    ensureCurrent: flow(function* (id: string, force?: boolean) {
      if (!force) {
        if (self.current?.kristall_id === id) {
          return self.current;
        }

        const cached = self.list.find((crystal) => crystal.kristall_id === id);

        if (cached) {
          self.current = cast(getSnapshot(cached));

          return self.current;
        }
      }

      return yield self.getCrystalById(id);
    }),
  }));
